import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    linkedSignal,
    signal,
    ViewEncapsulation,
} from "@angular/core";
import { form } from "@angular/forms/signals";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { s25Resource } from "../../util/s25.resource";
import { NotificationPolicyService } from "../../services/notification.policy.service";
import { NotificationPolicyMapper } from "./notification.policy.mapper";
import { NotificationPolicyModel } from "./notification.policy.model";

@TypeManagerDecorator("s25-ng-notification-policy")
@Component({
    selector: "s25-ng-notification-policy",
    template: `
        @if (isLoading()) {
            <s25-ng-loading-inline-static />
        } @else {
            <div class="notification-policy--wrapper">
                @if (showEmptyPolicyMessage()) {
                    <p>No notification policy</p>
                    @if (showAddPolicy()) {
                        <button
                            type="button"
                            class="aw-button aw-button--outline c-margin-top--half"
                            (click)="beginPolicyEditing()"
                        >
                            Add notification policy
                        </button>
                    }
                }
                @if (showReadOnlyView()) {
                    <s25-ng-notification-policy-sections
                        [policyForm]="policyForm"
                        [layout]="layout()"
                        [readOnly]="true"
                        [submitAttempted]="submitAttempted()"
                        [editor]="facade"
                    />
                }
                @if (!readOnly() && !showEmptyPolicyMessage()) {
                    @if (validationMessage()) {
                        <p class="ngRed c-margin-bottom--single" role="alert">{{ validationMessage() }}</p>
                    }
                    <s25-ng-notification-policy-sections
                        [policyForm]="policyForm"
                        [layout]="layout()"
                        [readOnly]="false"
                        [submitAttempted]="submitAttempted()"
                        [editor]="facade"
                    />
                    @if (showRemove() && hasPolicyContent()) {
                        <div class="notification-policy__actions c-margin-top--half">
                            <button
                                type="button"
                                class="aw-button aw-button--danger--outline"
                                (click)="markPolicyRemoved()"
                            >
                                Remove notification policy
                            </button>
                        </div>
                    }
                }
            </div>
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
    styles: `
        .notification-policy__actions {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
        }
    `,
})
export class S25NotificationPolicyComponent implements NotificationPolicyModel.Editor {
    readonly readOnly = input(false);
    readonly showRemove = input(true);
    readonly layout = input<NotificationPolicyModel.LayoutConfig>(NotificationPolicyModel.defaultLayout());
    readonly fetchItemTypeId = input<NotificationPolicyModel.NotifyFetchItemTypeId | undefined>();
    readonly fetchItemId = input<number | undefined>();
    readonly fetchGeneration = input(0);
    readonly initialDraft = input<NotificationPolicyModel.FormModel | undefined>();
    readonly seededAsNew = input(true);
    readonly seedNonce = input(0);

    readonly validationMessage = signal("");
    readonly pickerError = signal("");
    readonly submitAttempted = signal(false);

    private readonly reseedNonce = signal(0);
    private readonly internalGeneration = signal(0);

    readonly fetchKey = computed(() => {
        const itemTypeId = this.fetchItemTypeId();
        const itemId = this.fetchItemId();
        if (!itemTypeId || !itemId) {
            return undefined;
        }
        return {
            itemTypeId,
            itemId,
            generation: this.fetchGeneration() + this.internalGeneration(),
        };
    });

    readonly policyResource = s25Resource({
        params: () => this.fetchKey(),
        loader: ({ params }) => NotificationPolicyService.get(params.itemTypeId, params.itemId),
    });

    /** Fetch ids win over a seeded draft so template load and manual edit do not both apply. */
    private readonly seedSession = computed(() => {
        this.reseedNonce();
        this.seedNonce();
        const fetch = this.fetchKey();
        if (fetch) {
            const apiPolicy = this.policyResource.value();
            if (apiPolicy === undefined) {
                return NotificationPolicyMapper.sessionFromDraft(NotificationPolicyMapper.emptyDraft(), true);
            }
            return NotificationPolicyMapper.sessionFromGetResponse(apiPolicy);
        }
        const draft = this.initialDraft();
        if (draft) {
            return NotificationPolicyMapper.sessionFromDraft(draft, this.seededAsNew());
        }
        return NotificationPolicyMapper.sessionFromDraft(NotificationPolicyMapper.emptyDraft(), true);
    });

    readonly formModel = linkedSignal<NotificationPolicyModel.FormBindingModel>(() =>
        NotificationPolicyMapper.draftForForm(this.seedSession().draft),
    );
    private readonly baseline = linkedSignal(() => this.seedSession().baseline);
    private readonly isNewPolicyState = linkedSignal(() => this.seedSession().isNewPolicy);

    readonly policyForm = form(this.formModel, (schema) => {
        NotificationPolicyModel.Validators.days(schema.days);
        NotificationPolicyModel.Validators.hours(schema.hours);
        NotificationPolicyModel.Validators.minutes(schema.minutes);
        NotificationPolicyModel.Validators.assignmentsRequired(schema.assignments, schema.removedPolicy);
        NotificationPolicyModel.Validators.quorum(schema.approvalType, schema.assignments, schema.removedPolicy);
    });

    readonly draft = computed(() => NotificationPolicyMapper.draftFromForm(this.formModel()));
    readonly hasPolicyContent = computed(() => NotificationPolicyMapper.draftHasPolicyContent(this.draft()));
    readonly dirty = computed(() => {
        const compareTo = this.baseline() ?? NotificationPolicyMapper.emptyDraft();
        return !NotificationPolicyMapper.draftsEqual(this.draft(), compareTo);
    });
    readonly isLoading = computed(() => {
        if (this.policyResource.isLoading()) {
            return true;
        }
        // ANG-5254: Treat a configured fetch with no value yet as loading so the editor does not mount before GET.
        return !!this.fetchKey() && this.policyResource.value() === undefined && !this.policyResource.error();
    });

    readonly showReadOnlyView = computed(() => this.readOnly() && this.hasPolicyContent());
    readonly showEmptyPolicyMessage = computed(() => {
        if (this.hasPolicyContent()) {
            return false;
        }
        if (this.readOnly()) {
            return true;
        }
        return NotificationPolicyMapper.isRemovedPolicy(this.draft());
    });
    readonly showAddPolicy = computed(
        () => !this.readOnly() && !this.fetchKey() && NotificationPolicyMapper.isRemovedPolicy(this.draft()),
    );

    /** Stable object for the sections input. Methods live on this component. */
    readonly facade: NotificationPolicyModel.Editor = this;

    constructor() {
        inject(ElementRef).nativeElement.angBridge = this;
    }

    validate(): boolean {
        this.submitAttempted.set(true);
        const validation = NotificationPolicyMapper.validateDraft(this.draft());
        if (!validation.valid) {
            this.validationMessage.set(validation.message);
            return false;
        }
        if (!this.policyForm().valid()) {
            this.validationMessage.set(this.firstFieldError() || "Please correct the notification policy");
            return false;
        }
        this.replaceDraft(validation.draft);
        this.validationMessage.set("");
        return true;
    }

    getSession(): NotificationPolicyModel.Session {
        return {
            draft: this.draft(),
            baseline: this.baseline(),
            isNewPolicy: this.isNewPolicyState(),
        };
    }

    reset() {
        this.submitAttempted.set(false);
        this.validationMessage.set("");
        this.pickerError.set("");
        if (this.fetchKey()) {
            this.internalGeneration.update((generation) => generation + 1);
        }
        this.reseedNonce.update((nonce) => nonce + 1);
    }

    commitBaseline() {
        const committed = NotificationPolicyMapper.commitSessionAfterSave(this.getSession());
        this.replaceDraft(committed.draft);
        this.baseline.set(committed.baseline);
        this.isNewPolicyState.set(false);
    }

    markPolicyRemoved() {
        this.isNewPolicyState.set(true);
        this.submitAttempted.set(false);
        this.replaceDraft({
            ...NotificationPolicyMapper.emptyDraft(),
            removedPolicy: true,
            approvalType: { itemName: "No Notification Policy", itemId: NotificationPolicyMapper.REMOVED_ID },
        });
    }

    beginPolicyEditing() {
        this.isNewPolicyState.set(true);
        this.baseline.set(null);
        this.submitAttempted.set(false);
        this.replaceDraft(NotificationPolicyMapper.emptyDraftWithDefaults());
    }

    addContact(selected: NotificationPolicyModel.PickerContact, role: NotificationPolicyModel.ContactRole) {
        const contactId = S25Util.parseInt(selected.itemId);
        if (contactId == null) {
            return;
        }

        const draft = this.draft();
        const otherRole: NotificationPolicyModel.ContactRole = role === "approver" ? "fyi" : "approver";
        const duplicateOtherRole = draft.assignments.find(
            (assignment) => assignment.contactId === contactId && assignment.role === otherRole,
        );
        if (duplicateOtherRole) {
            this.pickerError.set(`${selected.itemName} already has a notification contact for this object`);
            return;
        }

        const alreadySameRole = draft.assignments.some(
            (assignment) => assignment.contactId === contactId && assignment.role === role,
        );
        if (alreadySameRole) {
            return;
        }

        const withoutContact = draft.assignments.filter((assignment) => assignment.contactId !== contactId);
        const withContact: NotificationPolicyModel.FormModel = {
            ...draft,
            assignments: [
                ...withoutContact,
                {
                    contactId,
                    contactName: selected.itemName ?? "",
                    role,
                },
            ],
            removedPolicy: false,
        };
        this.replaceDraft(NotificationPolicyMapper.ensureDefaultQuorum(withContact));
    }

    removeContact(contactId: number) {
        const draft = this.draft();
        const assignments = draft.assignments.filter((assignment) => assignment.contactId !== contactId);
        if (!assignments.length) {
            this.replaceDraft({ ...draft, assignments, approvalType: undefined });
            return;
        }
        this.replaceDraft({ ...draft, assignments });
    }

    moveContact(contactId: number, role: NotificationPolicyModel.ContactRole) {
        const draft = this.draft();
        this.replaceDraft({
            ...draft,
            assignments: draft.assignments.map((assignment) =>
                assignment.contactId === contactId ? { ...assignment, role } : assignment,
            ),
        });
    }

    setQuorum(approvalType: NotificationPolicyModel.ApprovalType) {
        this.replaceDraft({
            ...this.draft(),
            approvalType: approvalType ? { ...approvalType } : approvalType,
            removedPolicy: false,
        });
    }

    setApprovalWindowField(field: "days" | "hours" | "minutes", rawValue: number | string) {
        const parsed = S25Util.parseInt(rawValue) ?? 0;
        this.replaceDraft({ ...this.draft(), [field]: parsed, removedPolicy: false });
    }

    private replaceDraft(draft: NotificationPolicyModel.FormModel) {
        this.pickerError.set("");
        this.validationMessage.set("");
        this.formModel.set(NotificationPolicyMapper.draftForForm(draft));
    }

    private firstFieldError(): string {
        const fields = [
            this.policyForm.days,
            this.policyForm.hours,
            this.policyForm.minutes,
            this.policyForm.approvalType.itemId,
            this.policyForm.assignments,
        ];
        for (const field of fields) {
            if (!field) {
                continue;
            }
            const message = field().errors()[0]?.message;
            if (message) {
                return message;
            }
        }
        return "";
    }
}
